import { createBaselineProgress } from './catalog';
import { createSecureSeed, generatePilotQuestions } from './generator';
import { adoptLegacyProgress, loadPilotLearnerRecord, syncLearnerRecordFromDailyState } from './learnerRecordStore';
import { detectPilotMisconceptions, updatePilotSkillProgress } from './mastery';
import type { PilotAttemptRecord, PilotMathState } from './types';
import { localPilotRuntimeRepository, PILOT_UPDATED_EVENT } from '../runtime/localRuntimeRepository';
import { enqueueRuntimeEvent } from '../runtime/outbox';
import { buildTrustedAttemptSubmission } from '../runtime/trustedRuntime';

export { PILOT_UPDATED_EVENT };

function todayLocal() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createState(): PilotMathState {
  const seed = createSecureSeed();
  const learningDate = todayLocal();
  const createdAt = new Date().toISOString();
  const learner = loadPilotLearnerRecord();
  const progress = learner.progress ?? createBaselineProgress();
  return {
    schemaVersion: 2,
    student: learner.student,
    learningDate,
    assignmentId: `DAILY-MTH-${learningDate}-${seed.toString(16)}`,
    status: 'not_started',
    createdAt,
    currentIndex: 0,
    questions: generatePilotQuestions(seed, progress),
    attempts: [],
    progress,
  };
}

function isCurrentState(value: unknown): value is PilotMathState {
  if (!value || typeof value !== 'object') return false;
  return (value as { schemaVersion?: number }).schemaVersion === 2;
}

export function loadPilotState(): PilotMathState {
  if (typeof window === 'undefined') return createState();
  const parsed = localPilotRuntimeRepository.loadPilotState() as unknown;

  if (parsed && !isCurrentState(parsed)) {
    const legacy = parsed as { progress?: PilotMathState['progress'] };
    if (legacy.progress) adoptLegacyProgress(legacy.progress);
    localPilotRuntimeRepository.clearPilotState();
  }

  const current = isCurrentState(parsed) ? parsed : null;
  if (!current || current.learningDate !== todayLocal()) {
    const next = createState();
    savePilotState(next);
    syncLearnerRecordFromDailyState(next);
    return next;
  }
  return current;
}

export function savePilotState(state: PilotMathState) {
  if (typeof window === 'undefined') return;
  localPilotRuntimeRepository.savePilotState(state);
}

export function resetPilotState() {
  if (typeof window === 'undefined') return;
  localPilotRuntimeRepository.clearPilotState();
}

export function startPilot(state: PilotMathState): PilotMathState {
  if (state.status !== 'not_started') return state;
  const next = { ...state, status: 'in_progress' as const, startedAt: new Date().toISOString() };
  savePilotState(next);
  syncLearnerRecordFromDailyState(next);
  return next;
}

type FinalizeAttemptInput = Omit<
  PilotAttemptRecord,
  'previousMastery' | 'newMastery' | 'evidenceWeight' | 'misconceptionSignals' | 'reasonCode' | 'difficultyBand'
>;

export function finalizeAttempt(state: PilotMathState, attempt: FinalizeAttemptInput): PilotMathState {
  const question = state.questions.find((item) => item.id === attempt.questionId);
  if (!question) throw new Error(`Pilot question not found: ${attempt.questionId}`);

  const existing = state.progress[attempt.skillCode] ?? {
    skillCode: attempt.skillCode,
    label: attempt.skillLabel,
    masteryPercent: 0,
    state: 'available' as const,
    evidenceCount: 0,
    firstAttemptCorrect: 0,
    finalCorrect: 0,
    reviewPasses: 0,
    misconceptionSignals: 0,
  };

  const misconceptionSignals = detectPilotMisconceptions(question, attempt.firstResponse);
  const update = updatePilotSkillProgress({
    current: existing,
    question,
    firstAttemptCorrect: attempt.firstAttemptCorrect,
    finalCorrect: attempt.finalCorrect,
    correctionCount: attempt.correctionCount,
    hintCount: attempt.hintCount,
    completedAt: attempt.completedAt,
    misconceptionSignals,
  });

  const previousMastery = existing.masteryPercent;
  const newMastery = update.next.masteryPercent;
  const progress = { ...state.progress, [attempt.skillCode]: update.next };
  const currentIndex = Math.min(state.questions.length, state.currentIndex + 1);
  const complete = currentIndex >= state.questions.length;

  const record: PilotAttemptRecord = {
    ...attempt,
    reasonCode: question.reasonCode,
    difficultyBand: question.difficultyBand,
    previousMastery,
    newMastery,
    evidenceWeight: update.evidenceWeight,
    ...(misconceptionSignals.length > 0 ? { misconceptionSignals } : {}),
  };

  enqueueRuntimeEvent({
    id: `OUTBOX-${record.id}`,
    eventType: 'trusted_attempt_submission',
    studentId: state.student.id,
    payload: {
      schemaVersion: 1,
      submission: buildTrustedAttemptSubmission({
        studentRef: state.student.id,
        assignmentRef: state.assignmentId,
        question,
        attempt: record,
      }),
      ...(record.ink ? { inkDocument: record.ink } : {}),
    },
  });

  const next: PilotMathState = {
    ...state,
    status: complete ? 'completed' : 'in_progress',
    currentIndex,
    completedAt: complete ? attempt.completedAt : state.completedAt,
    attempts: [...state.attempts, record],
    progress,
  };
  savePilotState(next);
  syncLearnerRecordFromDailyState(next);
  return next;
}

export function pilotSummary(state: PilotMathState) {
  const total = state.attempts.length;
  const firstCorrect = state.attempts.filter((item) => item.firstAttemptCorrect).length;
  const totalActiveMs = state.attempts.reduce((sum, item) => sum + item.duration.activeDurationMs, 0);
  const totalElapsedMs = state.attempts.reduce((sum, item) => sum + item.duration.elapsedDurationMs, 0);
  const corrections = state.attempts.reduce((sum, item) => sum + item.correctionCount, 0);
  const misconceptionSignals = state.attempts.reduce((sum, item) => sum + (item.misconceptionSignals?.length ?? 0), 0);
  return {
    completed: total,
    totalQuestions: state.questions.length,
    firstAttemptAccuracy: total === 0 ? 0 : Math.round((firstCorrect / total) * 100),
    totalActiveMs,
    totalElapsedMs,
    corrections,
    misconceptionSignals,
  };
}
